import React from 'react';
import { Group, Text, NumberInput, ActionIcon, Tooltip } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import ClickableHelpText from './ClickableHelpText';

export interface McapBucket {
  min_mcap: number | null;
  max_mcap: number | null;
  min_percent: number | null;
  max_percent: number | null;
}

export interface McapBucketFilterProps {
  label: string;
  helpText: string;
  detailedInfo: string;
  value: McapBucket[] | null | undefined;
  onChange: (buckets: McapBucket[] | null) => void;
  maxBuckets?: number;
}

const theme = {
  background: {
    primary: '#07090E',
    secondary: '#161B22',
    tertiary: '#21262D',
    elevated: '#2D333B'
  },
  accent: {
    primary: '#4F46E5',
    secondary: '#7C3AED',
    tertiary: '#059669'
  },
  state: {
    success: '#10B981',
    error: '#EF4444',
    warning: '#F59E0B'
  },
  text: {
    primary: '#F9FAFB',
    secondary: '#9CA3AF',
    disabled: '#6B7280'
  },
  border: {
    primary: '#374151',
    secondary: '#4B5563'
  }
};

const McapBucketFilter: React.FC<McapBucketFilterProps> = ({
  label,
  helpText,
  detailedInfo,
  value,
  onChange,
  maxBuckets = 3
}) => {
  const buckets = value || [];

  const handleAddBucket = () => {
    if (buckets.length >= maxBuckets) return;
    const newBucket: McapBucket = {
      min_mcap: null,
      max_mcap: null,
      min_percent: null,
      max_percent: null
    };
    onChange([...buckets, newBucket]);
  };

  const handleRemoveBucket = (index: number) => {
    const newBuckets = buckets.filter((_, i) => i !== index);
    onChange(newBuckets.length > 0 ? newBuckets : null);
  };

  const handleBucketChange = (index: number, field: keyof McapBucket, val: number | '' | null) => {
    const newBuckets = [...buckets];
    newBuckets[index] = {
      ...newBuckets[index],
      [field]: val === '' || val === null ? null : Number(val)
    };
    onChange(newBuckets);
  };

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <Group mb={6} align="center" gap={4}>
        <Text size="xs" fw={600} c={theme.text.primary} style={{ textAlign: 'left' }}>
          {label}
        </Text>
        <ClickableHelpText text={helpText} detailedInfo={detailedInfo} />
        {buckets.length < maxBuckets && (
          <Tooltip label={`+bucket`} position="top" withArrow>
            <ActionIcon 
              size="xs" 
              variant="subtle" 
              color="green"
              onClick={handleAddBucket}
              style={{ marginLeft: 'auto' }}
            >
              <IconPlus size={12} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>

      {buckets.length === 0 && (
        <Text size="xs" c={theme.text.disabled} style={{ fontStyle: 'italic', textAlign: 'left' }}>
          No buckets
        </Text>
      )}

      {buckets.map((bucket, index) => (
        <div
          key={index}
          style={{
            background: theme.background.tertiary,
            border: `1px solid ${theme.border.primary}`,
            borderRadius: 4,
            padding: '4px 6px',
            marginBottom: 3
          }}
        >
          <Group mb={2} align="center" justify="space-between">
            <Text size="9px" fw={500} c={theme.text.disabled}>
              #{index + 1}
            </Text>
            <ActionIcon 
              size={14} 
              variant="subtle" 
              color="red"
              onClick={() => handleRemoveBucket(index)}
            >
              <IconTrash size={10} />
            </ActionIcon>
          </Group>

          {/* MCAP min-max */}
          <Text size="9px" c={theme.text.disabled} mb={2} ta="left">mcap</Text>
          <Group gap={4} mb={4} align="center" wrap="nowrap">
            <NumberInput
              placeholder="min"
              value={bucket.min_mcap ?? ''}
              onChange={(val) => handleBucketChange(index, 'min_mcap', val as number | '')}
              min={0}
              size="xs"
              styles={{
                root: { flex: 1, minWidth: 0 },
                input: {
                  backgroundColor: theme.background.secondary,
                  border: `1px solid ${theme.border.primary}`,
                  color: theme.text.primary,
                  minHeight: '22px',
                  fontSize: '10px',
                  padding: '1px 4px',
                }
              }}
              hideControls
            />
            <NumberInput
              placeholder="max"
              value={bucket.max_mcap ?? ''}
              onChange={(val) => handleBucketChange(index, 'max_mcap', val as number | '')}
              min={0}
              size="xs"
              styles={{
                root: { flex: 1, minWidth: 0 },
                input: {
                  backgroundColor: theme.background.secondary,
                  border: `1px solid ${theme.border.primary}`,
                  color: theme.text.primary,
                  minHeight: '22px',
                  fontSize: '10px',
                  padding: '1px 4px',
                }
              }}
              hideControls
            />
          </Group>

          {/* Percent min-max */}
          <Text size="9px" c={theme.text.disabled} mb={2}  ta="left">% tokens(optional)</Text>
          <Group gap={4} align="center" wrap="nowrap">
            <NumberInput
              placeholder="min"
              value={bucket.min_percent ?? ''}
              onChange={(val) => handleBucketChange(index, 'min_percent', val as number | '')}
              min={0}
              max={100}
              size="xs"
              styles={{
                root: { flex: 1, minWidth: 0 },
                input: {
                  backgroundColor: theme.background.secondary,
                  border: `1px solid ${theme.border.primary}`,
                  color: theme.text.primary,
                  minHeight: '22px',
                  fontSize: '10px',
                  padding: '1px 4px',
                }
              }}
              hideControls
            />
            <NumberInput
              placeholder="max"
              value={bucket.max_percent ?? ''}
              onChange={(val) => handleBucketChange(index, 'max_percent', val as number | '')}
              min={0}
              max={100}
              size="xs"
              styles={{
                root: { flex: 1, minWidth: 0 },
                input: {
                  backgroundColor: theme.background.secondary,
                  border: `1px solid ${theme.border.primary}`,
                  color: theme.text.primary,
                  minHeight: '22px',
                  fontSize: '10px',
                  padding: '1px 4px',
                }
              }}
              hideControls
            />
          </Group>
        </div>
      ))}
    </div>
  );
};

export default McapBucketFilter;
